import React, { useMemo, useRef } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { EventPageType } from '@graphql/generated-base';
import type { BoxProps } from '@withjoy/joykit';
import { NoSsr } from '@withjoy/joykit';
import type { LayoutAlohaProps } from '../../LayoutAloha';
import type { PreparedPages } from './Body.constants';
import { pageWidgets } from './Body.constants';
import Page from '../Page';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { SCREENS } from '@shared/types/fontPackScreens';
import { isLegacyOrMigratedStoryPage } from '@shared/utils/migratedCustomPages';
import { useHidesMapPageTitle } from '../../../../components/WeddingMapPageSection/useHidesMapPageTitle';

const EMPTY_APPLICATOR_PROPS = {};

export interface BodyProps
  extends Readonly<
    {
      readonly page: Maybe<EventPageFragment>;
      readonly applicatorProps?: object;
      readonly isWelcomePage?: boolean;
      /**
       * Honeymoon-redesign URL-deep-linked page bypasses <NoSsr> so the PageWidget renders SSR
       * (PRNT-4234). Resolved by LayoutAloha rather than here: it depends on the current URL, and
       * reading the URL inside each section subscribed all of them to the router, so Aloha's scroll
       * spy re-rendered every section of the site each time it rewrote the URL. As a prop it only
       * changes for the two sections that actually gained or lost the slug.
       */
      readonly isStaysRedesignCurrentPage?: boolean;
    } & LayoutAlohaProps &
      BoxProps
  > {}

const BodyComponent: React.FC<BodyProps> = ({ event, eventHandle, page, applicatorProps = EMPTY_APPLICATOR_PROPS, isWelcomePage, isStaysRedesignCurrentPage = false }) => {
  const pageRef = useRef<HTMLDivElement>(null);

  const onPageEditClick = useEventCallback(() => {
    if (page) {
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editPage',
          inlineEditData: {
            eventHandle,
            pageSlug: page.pageSlug,
            pageType: page.type,
            pageTitle: page.pageTitle,
            // CORE-6470: lets the editor route a userGenerated migrated squatter (pageKind === userCustom)
            // to the generic custom editor.
            pageKind: page.pageKind,
          },
        },
      });
    }
  });

  const handleEditFontClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { screen: SCREENS.categoryList } },
    });
  });

  const { shouldSuppressStoryPageNote, marginTop, isAccommodations } = useMemo(() => {
    return {
      // Suppress stale page note for legacy and system-migrated story (body lives elsewhere).
      shouldSuppressStoryPageNote: isLegacyOrMigratedStoryPage(page?.type, page?.pageSlug, page?.pageKind),
      marginTop: isWelcomePage ? '0' : '4rem',
      isAccommodations: page?.pageSlug === 'accommodations',
    };
  }, [page?.pageSlug, page?.type, page?.pageKind, isWelcomePage]);
  // The immersive map page brings no title or note of its own; see the hook.
  const hidesTitle = useHidesMapPageTitle(page?.type, page?.layoutType);

  if (!page) {
    return null;
  }

  const { type } = page;
  const PageWidget = pageWidgets[type as PreparedPages];

  const pageProps = {
    title: isWelcomePage || hidesTitle ? undefined : page.pageTitle,
    // Welcome pages: the greeting is rendered in the Aloha header (AuxFrameTitle / LegacyHeader);
    // rendering page.subTitle here as well duplicates it.
    subtitle: !shouldSuppressStoryPageNote && !isWelcomePage && !hidesTitle ? page.subTitle : undefined,
    graphicAccent: undefined,
    isAccommodations: isAccommodations,
    isWelcomePage,
    marginTop,
    photo: isWelcomePage ? undefined : page.photoV2,
  };

  if (!PageWidget) {
    return null;
  }

  const widget = (
    <PageWidget
      isAccommodations={isAccommodations}
      isWelcomePage={isWelcomePage}
      event={event}
      eventHandle={eventHandle}
      {...applicatorProps}
      pageId={page.id}
      pageSlug={page.pageSlug}
      pageTitle={pageProps.title}
    />
  );

  return (
    <InlineEditor
      actionData={{
        edit: onPageEditClick,
        font: handleEditFontClick,
      }}
      inset={{ x: 4, y: -16 }}
      wrapperType="actionOutside"
      componentName="page"
      elementLabel={page.pageTitle}
      pageName={page.type}
      pageSlug={page.pageSlug}
      stickyOnScroll={true}
      // No editing affordance for a title and note that are not shown.
      isEligibleForInlineEditing={page?.type !== EventPageType.app && !hidesTitle}
    >
      <Page
        {...pageProps}
        visibility={page.visibility}
        pageSlug={page.pageSlug}
        ref={pageRef}
        pageId={page.id}
        pageType={page.type}
      >
        {/* Welcome page + honeymoon-redesign current page render SSR; others NoSsr-wrapped (PR #14490, PRNT-4234). */}
        {isWelcomePage || isStaysRedesignCurrentPage ? widget : <NoSsr>{widget}</NoSsr>}
      </Page>
    </InlineEditor>
  );
};

/**
 * Memoised because LayoutAloha renders every section of the site at once, so any state change in
 * the layout — the scroll spy's current page, a breakpoint, a reveal — otherwise re-rendered all
 * of them. With stable props only the sections that actually changed re-render.
 */
export const Body = React.memo(BodyComponent);

Body.displayName = 'Body';
